import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  ImagePlus, Upload, Search, Filter, Trash2, Tags, X, Check,
  Loader, ExternalLink, Sparkles, Grid3X3, ChevronDown,
} from "lucide-react";
import api from "../api/client";

const IMAGE_TYPES = [
  { value: "project_photo", label: "Project Photo" },
  { value: "before_after", label: "Before & After" },
  { value: "team", label: "Team / Staff" },
  { value: "equipment", label: "Equipment" },
  { value: "generic", label: "Generic / Stock" },
  { value: "infographic", label: "Infographic" },
  { value: "logo", label: "Logo / Brand" },
];

function UploadModal({ siteId, onClose, onUploaded }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const dropRef = useRef(null);

  const handleFiles = (fileList) => {
    const allowed = ["image/jpeg", "image/png", "image/webp", "image/jpg"];
    const valid = Array.from(fileList).filter(
      (f) => allowed.includes(f.type) && f.size <= 10 * 1024 * 1024
    );
    if (valid.length > 20) valid.length = 20;
    setFiles(valid);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleFiles(e.dataTransfer.files);
  };

  const handleUpload = async () => {
    if (!files.length) return;
    setUploading(true);
    const formData = new FormData();
    files.forEach((f) => formData.append("images", f));

    try {
      const res = await api.post(
        `/websites/${siteId}/image-library/upload/`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setResults(res.data);
      onUploaded();
    } catch {
      setResults({ uploaded: 0, errors: [{ error: "Upload failed" }] });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-dark-300 border border-dark-50 rounded-2xl w-full max-w-xl max-h-[80vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-dark-50">
          <h3 className="text-base font-semibold">Upload Images</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-dark-50/50 rounded-lg"><X size={18} className="text-gray-400" /></button>
        </div>
        <div className="p-5 space-y-4">
          {!results ? (
            <>
              <div
                ref={dropRef}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDrop={handleDrop}
                className="border-2 border-dashed border-dark-50 rounded-xl p-8 text-center cursor-pointer hover:border-copper/50 transition-colors"
                onClick={() => document.getElementById("file-input").click()}
              >
                <Upload size={32} className="mx-auto text-gray-500 mb-3" />
                <div className="text-sm text-gray-400">
                  Drag & drop images here, or <span className="text-copper">browse</span>
                </div>
                <div className="text-xs text-gray-500 mt-1">JPG, PNG, WebP — Max 10MB per file, 20 files at once</div>
                <input
                  id="file-input"
                  type="file"
                  multiple
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => handleFiles(e.target.files)}
                />
              </div>

              {files.length > 0 && (
                <div className="space-y-2">
                  <div className="text-sm text-gray-300">{files.length} file{files.length > 1 ? "s" : ""} selected</div>
                  <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto">
                    {files.map((f, i) => (
                      <div key={i} className="relative">
                        <img
                          src={URL.createObjectURL(f)}
                          alt=""
                          className="w-full h-20 object-cover rounded-lg"
                        />
                        <button
                          onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                          className="absolute top-1 right-1 p-0.5 bg-black/60 rounded-full text-white"
                        >
                          <X size={10} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={handleUpload}
                    disabled={uploading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-copper hover:bg-copper-600 text-white font-medium rounded-lg disabled:opacity-50"
                  >
                    {uploading ? <Loader size={16} className="animate-spin" /> : <Upload size={16} />}
                    {uploading ? "Uploading..." : `Upload ${files.length} Image${files.length > 1 ? "s" : ""}`}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="space-y-3">
              <div className="text-center py-4">
                <Check size={32} className="mx-auto text-green-400 mb-2" />
                <div className="text-sm text-gray-300">{results.uploaded} image{results.uploaded !== 1 ? "s" : ""} uploaded</div>
                {results.errors?.length > 0 && (
                  <div className="text-xs text-red-400 mt-1">{results.errors.length} failed</div>
                )}
              </div>
              <button
                onClick={onClose}
                className="w-full px-4 py-2 bg-dark-50 text-gray-300 rounded-lg hover:text-white"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailModal({ image, siteId, onClose, onUpdate, onDelete }) {
  const [form, setForm] = useState({
    title: image.title || "",
    alt_text: image.alt_text || "",
    tags: (image.tags || []).join(", "),
    service_type: image.service_type || "",
    location: image.location || "",
    image_type: image.image_type || "project_photo",
  });
  const [saving, setSaving] = useState(false);
  const [genAlt, setGenAlt] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/websites/${siteId}/image-library/${image.id}/`, {
        ...form,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      onUpdate();
      onClose();
    } catch { /* ignore */ }
    setSaving(false);
  };

  const handleGenerateAlt = async () => {
    setGenAlt(true);
    try {
      const res = await api.post(`/websites/${siteId}/image-library/${image.id}/generate-alt/`);
      if (res.data.alt_text) {
        setForm((prev) => ({ ...prev, alt_text: res.data.alt_text }));
      }
    } catch { /* ignore */ }
    setGenAlt(false);
  };

  const handleDelete = () => {
    api.delete(`/websites/${siteId}/image-library/${image.id}/`)
      .then(() => { onDelete(image.id); onClose(); });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-dark-300 border border-dark-50 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-dark-50">
          <h3 className="text-base font-semibold truncate">{image.title}</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-dark-50/50 rounded-lg"><X size={18} className="text-gray-400" /></button>
        </div>
        <div className="p-5 space-y-4">
          {/* Preview */}
          <div className="bg-dark rounded-xl overflow-hidden flex items-center justify-center" style={{ maxHeight: "300px" }}>
            <img src={image.image_url || image.thumbnail_url} alt={image.alt_text || ""} className="max-w-full max-h-[300px] object-contain" />
          </div>

          {/* Info */}
          <div className="flex gap-4 text-xs text-gray-500">
            {image.width && <span>{image.width}x{image.height}px</span>}
            {image.file_size && <span>{(image.file_size / 1024).toFixed(0)} KB</span>}
            <span>{image.mime_type}</span>
            {image.wp_media_url && <span className="text-green-400">Uploaded to WP</span>}
          </div>

          {/* Form */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Title</label>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Alt Text</label>
              <div className="flex gap-2">
                <input value={form.alt_text} onChange={(e) => setForm({ ...form, alt_text: e.target.value })} className="flex-1 bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50" placeholder="Descriptive alt text for SEO" />
                <button onClick={handleGenerateAlt} disabled={genAlt} className="flex items-center gap-1 px-3 py-2 text-xs bg-copper/20 text-copper rounded-lg hover:bg-copper/30 disabled:opacity-50 whitespace-nowrap">
                  {genAlt ? <Loader size={12} className="animate-spin" /> : <Sparkles size={12} />}
                  AI Generate
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Service Type</label>
              <input value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })} className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50" placeholder="e.g. bathroom_renovation" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Location</label>
              <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50" placeholder="e.g. Scarsdale" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Image Type</label>
              <select value={form.image_type} onChange={(e) => setForm({ ...form, image_type: e.target.value })} className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50">
                {IMAGE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Tags (comma-separated)</label>
              <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50" placeholder="bathroom, renovation, modern" />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-between pt-2 border-t border-dark-50">
            <button onClick={handleDelete} className="flex items-center gap-1.5 px-3 py-2 text-xs text-red-400 hover:bg-red-900/20 rounded-lg">
              <Trash2 size={13} /> Delete
            </button>
            <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-copper hover:bg-copper-600 text-white text-sm font-medium rounded-lg disabled:opacity-50">
              {saving ? <Loader size={14} className="animate-spin" /> : <Check size={14} />}
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BulkTagModal({ count, siteId, selectedIds, onClose, onDone }) {
  const [form, setForm] = useState({ service_type: "", location: "", image_type: "", tags: "" });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post(`/websites/${siteId}/image-library/bulk-tag/`, {
        ids: selectedIds,
        ...form,
        tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
      });
      onDone();
      onClose();
    } catch { /* ignore */ }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-dark-300 border border-dark-50 rounded-2xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-dark-50">
          <h3 className="text-base font-semibold">Bulk Tag {count} Images</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-dark-50/50 rounded-lg"><X size={18} className="text-gray-400" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Service Type</label>
            <input value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })} className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Location</label>
            <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Image Type</label>
            <select value={form.image_type} onChange={(e) => setForm({ ...form, image_type: e.target.value })} className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50">
              <option value="">— No change —</option>
              {IMAGE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Add Tags (comma-separated)</label>
            <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50" placeholder="bathroom, modern, renovation" />
          </div>
          <button onClick={handleSave} disabled={saving} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-copper hover:bg-copper-600 text-white text-sm font-medium rounded-lg disabled:opacity-50">
            {saving ? <Loader size={14} className="animate-spin" /> : <Tags size={14} />}
            Apply to {count} Images
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ImageLibrary({ selectedSite }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [detailImage, setDetailImage] = useState(null);
  const [showBulkTag, setShowBulkTag] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [serviceFilter, setServiceFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");

  // Selection
  const [selected, setSelected] = useState(new Set());
  const [bulkAction, setBulkAction] = useState("");

  const fetchImages = useCallback(() => {
    if (!selectedSite) return;
    setLoading(true);
    const params = {};
    if (searchQuery) params.search = searchQuery;
    if (typeFilter) params.image_type = typeFilter;
    if (serviceFilter) params.service_type = serviceFilter;
    if (locationFilter) params.location = locationFilter;

    api.get(`/websites/${selectedSite.id}/image-library/`, { params })
      .then((r) => setImages(r.data.results || []))
      .catch(() => setImages([]))
      .finally(() => setLoading(false));
  }, [selectedSite, searchQuery, typeFilter, serviceFilter, locationFilter]);

  useEffect(() => { fetchImages(); }, [fetchImages]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === images.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(images.map((i) => i.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (!selected.size) return;
    setBulkAction("deleting");
    try {
      await api.post(`/websites/${selectedSite.id}/image-library/bulk-delete/`, { ids: [...selected] });
      setSelected(new Set());
      fetchImages();
    } catch { /* ignore */ }
    setBulkAction("");
  };

  const handleBulkUploadWP = async () => {
    if (!selected.size) return;
    setBulkAction("uploading");
    try {
      await api.post(`/websites/${selectedSite.id}/image-library/upload-to-wp/`, { ids: [...selected] });
      setSelected(new Set());
      fetchImages();
    } catch { /* ignore */ }
    setBulkAction("");
  };

  // Derive unique service types and locations for filters
  const serviceTypes = useMemo(() => [...new Set(images.map((i) => i.service_type).filter(Boolean))], [images]);
  const locations = useMemo(() => [...new Set(images.map((i) => i.location).filter(Boolean))], [images]);

  if (!selectedSite) {
    return <div className="text-gray-500 text-center py-20">Select a website</div>;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <ImagePlus size={20} className="text-copper" /> Image Library
        </h2>
        <button
          onClick={() => setShowUpload(true)}
          className="flex items-center gap-2 px-4 py-2 bg-copper hover:bg-copper-600 text-white text-sm font-medium rounded-lg"
        >
          <Upload size={14} /> Upload Images
        </button>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by title, tag, alt text..."
            className="w-full bg-dark-300 border border-dark-50 rounded-lg pl-9 pr-4 py-2 text-sm focus:outline-none focus:border-copper/50"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="bg-dark-300 border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50"
        >
          <option value="">All Types</option>
          {IMAGE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        {serviceTypes.length > 0 && (
          <select
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value)}
            className="bg-dark-300 border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50"
          >
            <option value="">All Services</option>
            {serviceTypes.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        {locations.length > 0 && (
          <select
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            className="bg-dark-300 border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50"
          >
            <option value="">All Locations</option>
            {locations.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        )}
      </div>

      {/* Bulk actions */}
      {selected.size > 0 && (
        <div className="flex items-center gap-2 bg-dark-300 border border-copper/30 rounded-xl px-4 py-2.5">
          <span className="text-sm text-gray-300">{selected.size} selected</span>
          <span className="w-px h-5 bg-dark-50 mx-1" />
          <button
            onClick={() => setShowBulkTag(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-blue-900/20 text-blue-400 rounded-lg hover:bg-blue-900/30"
          >
            <Tags size={12} /> Bulk Tag
          </button>
          <button
            onClick={handleBulkUploadWP}
            disabled={!!bulkAction}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-green-900/20 text-green-400 rounded-lg hover:bg-green-900/30 disabled:opacity-50"
          >
            <ExternalLink size={12} /> {bulkAction === "uploading" ? "Uploading..." : "Upload to WP"}
          </button>
          <button
            onClick={handleBulkDelete}
            disabled={!!bulkAction}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-red-900/20 text-red-400 rounded-lg hover:bg-red-900/30 disabled:opacity-50"
          >
            <Trash2 size={12} /> {bulkAction === "deleting" ? "Deleting..." : "Delete"}
          </button>
          <button
            onClick={() => setSelected(new Set())}
            className="ml-auto text-xs text-gray-500 hover:text-gray-300"
          >
            Clear
          </button>
        </div>
      )}

      {/* Select all toggle */}
      {images.length > 0 && (
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
            <input
              type="checkbox"
              checked={selected.size === images.length && images.length > 0}
              onChange={toggleSelectAll}
              className="w-3.5 h-3.5 rounded border-dark-50 bg-dark text-copper focus:ring-copper/50"
            />
            Select all ({images.length})
          </label>
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div key={i} className="h-48 bg-dark-300 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : images.length === 0 ? (
        <div className="text-center py-16">
          <ImagePlus size={40} className="mx-auto text-gray-600 mb-3" />
          <div className="text-gray-500">No images yet. Upload your first images to get started.</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {images.map((img) => (
            <div
              key={img.id}
              className={`bg-dark-300 border rounded-xl overflow-hidden cursor-pointer transition-all hover:border-copper/50 group ${
                selected.has(img.id) ? "border-copper ring-1 ring-copper/30" : "border-dark-50"
              }`}
            >
              {/* Checkbox */}
              <div className="relative">
                <div
                  className="absolute top-2 left-2 z-10"
                  onClick={(e) => { e.stopPropagation(); toggleSelect(img.id); }}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(img.id)}
                    onChange={() => {}}
                    className="w-4 h-4 rounded border-dark-50 bg-dark/80 text-copper focus:ring-copper/50 cursor-pointer"
                  />
                </div>
                {img.wp_media_url && (
                  <div className="absolute top-2 right-2 z-10 px-1.5 py-0.5 bg-green-900/80 text-green-400 text-[10px] rounded font-medium">
                    WP
                  </div>
                )}
                <div
                  onClick={() => setDetailImage(img)}
                  className="h-40 bg-dark-50 flex items-center justify-center overflow-hidden"
                >
                  <img
                    src={img.thumbnail_url || img.image_url}
                    alt={img.alt_text || ""}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                </div>
              </div>
              <div className="p-2.5" onClick={() => setDetailImage(img)}>
                <div className="text-sm text-gray-200 font-medium truncate">{img.title}</div>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {img.service_type && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-copper/15 text-copper rounded">{img.service_type}</span>
                  )}
                  {img.location && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-blue-900/20 text-blue-400 rounded">{img.location}</span>
                  )}
                  {(img.tags || []).slice(0, 2).map((tag, i) => (
                    <span key={i} className="text-[10px] px-1.5 py-0.5 bg-dark-50 text-gray-500 rounded">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {showUpload && (
        <UploadModal
          siteId={selectedSite.id}
          onClose={() => setShowUpload(false)}
          onUploaded={fetchImages}
        />
      )}
      {detailImage && (
        <DetailModal
          image={detailImage}
          siteId={selectedSite.id}
          onClose={() => setDetailImage(null)}
          onUpdate={fetchImages}
          onDelete={(id) => {
            setImages((prev) => prev.filter((i) => i.id !== id));
            setSelected((prev) => { const next = new Set(prev); next.delete(id); return next; });
          }}
        />
      )}
      {showBulkTag && (
        <BulkTagModal
          count={selected.size}
          siteId={selectedSite.id}
          selectedIds={[...selected]}
          onClose={() => setShowBulkTag(false)}
          onDone={() => { fetchImages(); setSelected(new Set()); }}
        />
      )}
    </div>
  );
}
